import type { Session } from '@supabase/supabase-js';
import { getCachedSession } from '../context/AuthContext.js';

const RETURN_KEY = 'ocn.github.connection-return';
const RETURN_TTL = 600_000;
const FAILURE_MESSAGE = 'GitHub connection could not be verified. Retry or use a repository access token.';

/** The JWT and provider credential must belong to the same still-current subscriber. */
export async function saveGitHubConnection(token: string, session: Pick<Session, 'access_token' | 'user'>, userId: string): Promise<void> {
  const current = getCachedSession();
  if (!token || session.user.id !== userId || current?.user.id !== userId
    || current.access_token !== session.access_token) throw new Error(FAILURE_MESSAGE);
  try {
    const response = await fetch((import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev') + '/api/user/github-token', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ github_token: token }), signal: AbortSignal.timeout(10_000), redirect: 'error',
    });
    if (!response.ok) throw new Error(FAILURE_MESSAGE);
  } catch {
    // Never expose provider/request exceptions or bodies to the UI or browser logs.
    throw new Error(FAILURE_MESSAGE);
  }
}

/** Only non-secret, same-tab return intent crosses the OAuth redirect. */
export function rememberGitHubConnection(userId: string, projectId?: string): void {
  sessionStorage.setItem(RETURN_KEY, JSON.stringify({ userId, projectId, expires: Date.now() + RETURN_TTL }));
}
export function clearGitHubConnectionReturn(): void {
  try { sessionStorage.removeItem(RETURN_KEY); } catch { /* No persisted return can be recovered. */ }
}
export function consumeGitHubConnection(userId: string): { sameUser: boolean; path: string } | null {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    if (!raw) return null;
    const failure = { sameUser: false, path: '/?page=config' };
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return failure;
    const row = value as Record<string, unknown>;
    if (typeof row.userId !== 'string' || typeof row.expires !== 'number'
      || row.expires <= Date.now() || row.expires > Date.now() + RETURN_TTL) return failure;
    const query = new URLSearchParams({ page: 'config' });
    if (typeof row.projectId === 'string') query.set('projectId', row.projectId);
    return { sameUser: row.userId === userId, path: '/?' + query.toString() };
  } catch {
    // Storage/parse failures with a pending return must not become normal token capture.
    return { sameUser: false, path: '/?page=config' };
  }
}
